import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "fr" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  // A simple translation helper function. We will use it for common small strings,
  // and for large blocks of text, we will just use conditional rendering.
  t: (frStr: string, enStr: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>("fr");

  // Load from localStorage if available
  useEffect(() => {
    const saved = localStorage.getItem("avisium-lang") as Language;
    if (saved === "fr" || saved === "en") {
      setLanguage(saved);
    }
  }, []);

  // Update HTML lang attribute
  useEffect(() => {
    document.documentElement.setAttribute("lang", language);
  }, [language]);

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem("avisium-lang", lang);
  };

  const t = (frStr: string, enStr: string) => {
    return language === "en" ? enStr : frStr;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
